import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiTruck, FiPlus, FiSearch, FiFilter, FiEdit2, FiEye, FiTrash2 } from 'react-icons/fi';
import { AdminLayout } from '../../components/layout';
import { Card, Button, Loading, Modal } from '../../components/common';
import { getAllCars, deleteCar } from '../../firebase/cars';
import { getAllCompanies } from '../../firebase/companies';

const AdminCarList = () => {
  const [loading, setLoading] = useState(true);
  const [cars, setCars] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [carToDelete, setCarToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [carsResult, companiesResult] = await Promise.all([
        getAllCars(),
        getAllCompanies()
      ]);
      
      if (carsResult.cars) {
        setCars(carsResult.cars);
      }
      if (companiesResult.companies) {
        setCompanies(companiesResult.companies);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get company name by ID
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown';
  };

  // Handle delete confirmation
  const handleDeleteClick = (car) => {
    setCarToDelete(car);
  };

  // Handle delete confirmation cancel
  const handleDeleteCancel = () => {
    setCarToDelete(null);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!carToDelete) return;
    
    setDeleting(true);
    try {
      const result = await deleteCar(carToDelete.id);
      
      if (result.error) {
        alert(`Error deleting car: ${result.error}`);
      } else {
        // Remove car from local state
        setCars(prevCars => prevCars.filter(car => car.id !== carToDelete.id));
        setCarToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting car:', error);
      alert(`Error deleting car: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Filter cars
  const filteredCars = cars.filter(car => {
    const matchesSearch = 
      car.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      car.brand?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCompany = selectedCompany === 'all' || car.companyId === selectedCompany;
    const matchesStatus = selectedStatus === 'all' || car.status === selectedStatus;
    
    return matchesSearch && matchesCompany && matchesStatus;
  });

  if (loading) {
    return (
      <AdminLayout title="All Cars">
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="All Cars">
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Cars</h1>
          <p className="text-gray-600">
            {filteredCars.length} car{filteredCars.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <Link to="/admin/cars/new">
          <Button className="flex items-center gap-2">
            <FiPlus size={18} />
            Add Car
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by car name or brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Company Filter */}
          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-400" />
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Companies</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </div>
      </Card>

      {/* Cars List */}
      {filteredCars.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiTruck className="text-gray-400" size={32} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No cars found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || selectedCompany !== 'all' || selectedStatus !== 'all'
              ? 'Try adjusting your filters'
              : 'No cars have been added yet'}
          </p>
          <Link to="/admin/cars/new">
            <Button>Add First Car</Button>
          </Link>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredCars.map(car => (
            <Card key={car.id} className="p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Car Image */}
                <div className="w-full md:w-32 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {car.images?.[0] ? (
                    <img 
                      src={car.images[0]} 
                      alt={car.name}
                      className="w-full h-full object-contain bg-gray-100"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FiTruck className="text-gray-400" size={32} />
                    </div>
                  )}
                </div>

                {/* Car Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{car.name}</h3>
                      <p className="text-sm text-gray-600">
                        {car.brand} {car.model && `• ${car.model}`}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      car.status === 'active' 
                        ? 'bg-green-100 text-green-700'
                        : car.status === 'maintenance'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {car.status}
                    </span>
                  </div>
                  
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {car.seats} Seats
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {car.transmission}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {car.fuelType}
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                      {getCompanyName(car.companyId)}
                    </span>
                  </div>
                  
                  <div className="mt-2 flex items-center gap-4 text-sm">
                    <span className="text-gray-600">
                      12hr: <span className="font-semibold text-gray-900">₹{car.price12Hour?.toLocaleString()}</span>
                    </span>
                    <span className="text-gray-600">
                      24hr: <span className="font-semibold text-gray-900">₹{car.price24Hour?.toLocaleString()}</span>
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Link to={`/car/${car.id}`}>
                    <Button variant="outline" size="sm" className="flex items-center gap-1">
                      <FiEye size={14} />
                      View
                    </Button>
                  </Link>
                  <Link to={`/admin/cars/${car.id}/edit`}>
                    <Button variant="outline" size="sm" className="flex items-center gap-1">
                      <FiEdit2 size={14} />
                      Edit
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:border-red-300"
                    onClick={() => handleDeleteClick(car)}
                  >
                    <FiTrash2 size={14} />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!carToDelete}
        onClose={handleDeleteCancel}
        title="Delete Car"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete <strong>{carToDelete?.name}</strong>?
          </p>
          <p className="text-sm text-gray-500">
            This action cannot be undone. The car will be permanently removed from the database.
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? <Loading size="sm" /> : 'Delete Car'}
            </Button>
          </div>
        </div>
      </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminCarList;
